import {
  Alert,
  Box,
  Container,
  Autocomplete,
  TextField,
  Button,
  Stack,
  Chip,
  Typography,
  Card,
  CardContent,
  Paper,
  Fade,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import BackBtn from "../../widgets/BackBtn";
import useApiEp from "../../hooks/useApiEp";
import MuPb from "../../widgets/MuPb";
import { getErrorResponse, getHeader } from "../../utils/util";
import axios from "axios";
import {
  Users,
  UserCheck,
  Shield,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Filter,
  Plus,
  X,
  Loader,
} from "lucide-react";
import { API_ENDPOINT } from "../../utils/constants";

const AccountGroupsManage = () => {
  const {
    data: allCustomerIds,
    isLoading,
    isError,
    error,
  } = useApiEp(`/api/customer-ids`);
  const { data: primaryAccounts, isLoading: isPrimaryLoading } = useApiEp(
    `/user/primary-accounts`
  );

  const [selectedPrimary, setSelectedPrimary] = useState(null);
  const [associatedAccounts, setAssociatedAccounts] = useState([]);
  const [loadingAssociated, setLoadingAssociated] = useState(false);
  const [accountsToAdd, setAccountsToAdd] = useState([]);
  const [accountsToRemove, setAccountsToRemove] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Load associated accounts when primary account is selected
  useEffect(() => {
    const loadAssociatedAccounts = async () => {
      if (!selectedPrimary) {
        setAssociatedAccounts([]);
        setAccountsToAdd([]);
        setAccountsToRemove([]);
        return;
      }

      setLoadingAssociated(true);
      try {
        const response = await axios.get(
          `${API_ENDPOINT}/user/associated-accounts/${selectedPrimary}`,
          getHeader()
        );
        setAssociatedAccounts(response.data || []);
        setAccountsToAdd([]);
        setAccountsToRemove([]);
      } catch (err) {
        console.error("Error loading associated accounts:", err);
        setAssociatedAccounts([]);
      } finally {
        setLoadingAssociated(false);
      }
    };

    loadAssociatedAccounts();
  }, [selectedPrimary]);

  // Get available accounts to add (exclude primary and already associated)
  const availableToAdd =
    allCustomerIds?.filter(
      (id) =>
        id !== selectedPrimary &&
        !associatedAccounts.includes(id) &&
        !accountsToAdd.includes(id)
    ) || [];

  // Get current associated accounts minus those marked for removal
  const currentAssociatedAccounts = [
    ...associatedAccounts.filter((id) => !accountsToRemove.includes(id)),
    ...accountsToAdd,
  ];

  const handleAddAccount = (account) => {
    if (account && !accountsToAdd.includes(account)) {
      setAccountsToAdd([...accountsToAdd, account]);
    }
  };

  const handleRemoveNewAccount = (account) => {
    setAccountsToAdd(accountsToAdd.filter((id) => id !== account));
  };

  const handleMarkForRemoval = (account) => {
    if (!accountsToRemove.includes(account)) {
      setAccountsToRemove([...accountsToRemove, account]);
    }
  };

  const handleUnmarkForRemoval = (account) => {
    setAccountsToRemove(accountsToRemove.filter((id) => id !== account));
  };

  const handleSubmit = async () => {
    if (!selectedPrimary) {
      setSubmitError("Please select a primary account");
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    // Calculate final accounts list
    const finalAccounts = [
      selectedPrimary,
      ...associatedAccounts.filter((id) => !accountsToRemove.includes(id)),
      ...accountsToAdd,
    ];

    const payload = {
      accounts: finalAccounts,
      assignedTo: selectedPrimary,
    };

    try {
      await axios.post(
        `${API_ENDPOINT}/root/assign-accounts`,
        payload,
        getHeader()
      );
      setSubmitSuccess(true);
      // Reset after successful submission
      setTimeout(() => {
        setSelectedPrimary(null);
        setAssociatedAccounts([]);
        setAccountsToAdd([]);
        setAccountsToRemove([]);
        setSubmitSuccess(false);
      }, 2000);
    } catch (err) {
      setSubmitError(getErrorResponse(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setSelectedPrimary(null);
    setAssociatedAccounts([]);
    setAccountsToAdd([]);
    setAccountsToRemove([]);
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  const hasChanges = accountsToAdd.length > 0 || accountsToRemove.length > 0;

  if (isLoading || isPrimaryLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <MuPb />
      </Box>
    );
  }

  if (isError) {
    return (
      <Container sx={{ mt: 3 }}>
        <Alert severity="error" icon={<AlertCircle size={20} />} sx={{ mb: 2 }}>
          {getErrorResponse(error)}
        </Alert>
        <BackBtn />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 3, mb: 3 }}>
      <Box sx={{ mb: 3 }}>
        <BackBtn />
      </Box>

      <Card elevation={2} sx={{ borderRadius: 2, overflow: "visible" }}>
        <CardContent sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 64,
                height: 64,
                borderRadius: "50%",
                bgcolor: "primary.light",
                color: "primary.main",
                mb: 2,
              }}
            >
              <Shield size={32} />
            </Box>
            <Typography variant="h4" gutterBottom fontWeight="600">
              Account Groups Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Select a primary account and manage its associated accounts
            </Typography>
          </Box>

          <Stack spacing={4}>
            {/* Primary Account Selection */}
            <Box>
              <Typography
                variant="h6"
                gutterBottom
                display="flex"
                alignItems="center"
                gap={1}
              >
                <UserCheck size={20} />
                Select Primary Account
              </Typography>
              <Autocomplete
                options={primaryAccounts || []}
                value={selectedPrimary}
                onChange={(e, newValue) => setSelectedPrimary(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Choose primary account"
                    placeholder="Search for primary account..."
                    helperText="Select an existing primary account or choose any customer ID"
                  />
                )}
              />
            </Box>

            {/* Loading Associated Accounts */}
            {loadingAssociated && (
              <Box
                display="flex"
                alignItems="center"
                gap={2}
                justifyContent="center"
                py={2}
              >
                <Loader size={20} className="spin" />
                <Typography variant="body2" color="text.secondary">
                  Loading associated accounts...
                </Typography>
              </Box>
            )}

            {/* Associated Accounts Section */}
            {selectedPrimary && !loadingAssociated && (
              <>
                <Divider />

                {/* Current Associated Accounts */}
                <Box>
                  <Typography
                    variant="h6"
                    gutterBottom
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    <Users size={20} />
                    Associated Accounts
                    {currentAssociatedAccounts.length > 0 && (
                      <Chip
                        label={currentAssociatedAccounts.length}
                        size="small"
                        color="primary"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Typography>

                  {currentAssociatedAccounts.length === 0 ? (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        bgcolor: "grey.50",
                        border: "1px dashed",
                        borderColor: "grey.300",
                        borderRadius: 2,
                        textAlign: "center",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No associated accounts. Add accounts below.
                      </Typography>
                    </Paper>
                  ) : (
                    <Stack spacing={1}>
                      {/* Original Associated Accounts */}
                      {associatedAccounts.map((account) => (
                        <Paper
                          key={account}
                          elevation={0}
                          sx={{
                            p: 2,
                            border: "1px solid",
                            borderColor: accountsToRemove.includes(account)
                              ? "error.main"
                              : "grey.300",
                            borderRadius: 1,
                            bgcolor: accountsToRemove.includes(account)
                              ? "error.50"
                              : "background.paper",
                            opacity: accountsToRemove.includes(account)
                              ? 0.6
                              : 1,
                          }}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Box display="flex" alignItems="center" gap={1}>
                              <Users size={16} />
                              <Typography
                                sx={{
                                  textDecoration: accountsToRemove.includes(
                                    account
                                  )
                                    ? "line-through"
                                    : "none",
                                }}
                              >
                                {account}
                              </Typography>
                              {accountsToRemove.includes(account) && (
                                <Chip
                                  label="To Remove"
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                            {accountsToRemove.includes(account) ? (
                              <Button
                                size="small"
                                onClick={() => handleUnmarkForRemoval(account)}
                                startIcon={<RefreshCw size={16} />}
                              >
                                Undo
                              </Button>
                            ) : (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleMarkForRemoval(account)}
                              >
                                <X size={18} />
                              </IconButton>
                            )}
                          </Box>
                        </Paper>
                      ))}

                      {/* Newly Added Accounts */}
                      {accountsToAdd.map((account) => (
                        <Paper
                          key={account}
                          elevation={0}
                          sx={{
                            p: 2,
                            border: "1px solid",
                            borderColor: "success.main",
                            borderRadius: 1,
                            bgcolor: "success.50",
                          }}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Box display="flex" alignItems="center" gap={1}>
                              <Users size={16} />
                              <Typography>{account}</Typography>
                              <Chip
                                label="New"
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            </Box>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveNewAccount(account)}
                            >
                              <X size={18} />
                            </IconButton>
                          </Box>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Box>

                {/* Add New Account */}
                <Box>
                  <Typography
                    variant="h6"
                    gutterBottom
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    <Plus size={20} />
                    Add Account
                  </Typography>
                  <Autocomplete
                    options={availableToAdd}
                    onChange={(e, newValue) => {
                      if (newValue) handleAddAccount(newValue);
                    }}
                    value={null}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select account to add"
                        placeholder="Search and add accounts..."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <Filter
                                size={18}
                                style={{ marginRight: 8, color: "#666" }}
                              />
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Box>

                {/* Changes Summary */}
                {hasChanges && (
                  <Fade in>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: "info.50",
                        border: "1px solid",
                        borderColor: "info.200",
                        borderRadius: 2,
                      }}
                    >
                      <Typography variant="body2" fontWeight="500" gutterBottom>
                        Pending Changes:
                      </Typography>
                      <Stack spacing={0.5}>
                        {accountsToAdd.length > 0 && (
                          <Typography variant="body2" color="success.main">
                            • {accountsToAdd.length} account(s) to add
                          </Typography>
                        )}
                        {accountsToRemove.length > 0 && (
                          <Typography variant="body2" color="error.main">
                            • {accountsToRemove.length} account(s) to remove
                          </Typography>
                        )}
                      </Stack>
                    </Paper>
                  </Fade>
                )}
              </>
            )}

            {/* Alerts */}
            {submitError && (
              <Fade in>
                <Alert
                  severity="error"
                  onClose={() => setSubmitError(null)}
                  icon={<AlertCircle size={20} />}
                >
                  {submitError}
                </Alert>
              </Fade>
            )}

            {submitSuccess && (
              <Fade in>
                <Alert severity="success" icon={<CheckCircle size={20} />}>
                  Account group updated successfully!
                </Alert>
              </Fade>
            )}

            {/* Action Buttons */}
            {selectedPrimary && (
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  disabled={submitting}
                  startIcon={<RefreshCw size={18} />}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={submitting}
                  size="large"
                  startIcon={
                    submitting ? (
                      <Loader size={18} className="spin" />
                    ) : (
                      <ArrowRight size={18} />
                    )
                  }
                  sx={{ minWidth: 160 }}
                >
                  {submitting ? "Saving..." : "Save Changes"}
                </Button>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </Container>
  );
};

export default AccountGroupsManage;
